import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
	Modal,
	ModalTrigger,
	ModalContent,
	ModalHeader,
	ModalTitle,
	ModalDescription,
	ModalFooter,
	ModalClose,
	Button,
	FormField,
	Input,
	CardContent,
	Alert
} from '../ui';
import { useClientCreate } from '../features/clients';
import { useData } from '../../contexts/DataContext';

/**
 * AddClientForm - Modal form for creating new clients
 * 
 * Follows the design system principles with clean separation of concerns:
 * - Uses UI primitives for consistent styling
 * - Uses business logic hook for data management
 * - Handles form state and validation
 * - Provides proper error handling and loading states
 */
export const AddClientForm = forwardRef(function AddClientForm({ trigger, onSuccess, isOnboarding = false, isOpen: externalIsOpen, onOpenChange: externalOnOpenChange, onValidityChange }, ref) {
	const [internalIsOpen, setInternalIsOpen] = useState(false);

	// Use external state if provided, otherwise use internal state
	const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
	const setIsOpen = externalOnOpenChange || setInternalIsOpen;
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		phone: '',
		category: 'individual',
		priority: 'medium',
		notes: ''
	});

	const { fetchClients } = useData();
	const { createClient, loading, error, clearError } = useClientCreate();

	// Expose submit function for wizard
	useImperativeHandle(ref, () => ({
		submit: handleSubmit
	}));

	// Report initial validity to parent if in onboarding mode
	useEffect(() => {
		if (isOnboarding && onValidityChange) {
			const isValid = validateForm().valid;
			onValidityChange(isValid);
		}
		// Runs once when onboarding mode is entered. onValidityChange is a fresh
		// function every parent render; depending on it would re-report validity
		// on every keystroke of the parent and loop.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isOnboarding]);

	const categoryOptions = [
		{ value: 'individual', label: 'Individual' },
		{ value: 'influencer', label: 'Influencer' },
		{ value: 'brand', label: 'Brand' },
		{ value: 'business', label: 'Business' },
		{ value: 'other', label: 'Other' }
	];

	const priorityOptions = [
		{ value: 'low', label: 'Low' },
		{ value: 'medium', label: 'Medium' },
		{ value: 'high', label: 'High' },
		{ value: 'critical', label: 'Critical' }
	];

	const handleInputChange = (field, value) => {
		const updatedFormData = {
			...formData,
			[field]: value
		};

		setFormData(updatedFormData);

		// Clear error when user starts typing
		if (error) clearError();

		// Report validity to parent if in onboarding mode
		if (isOnboarding && onValidityChange) {
			const isValid = validateFormWithData(updatedFormData).valid;
			onValidityChange(isValid);
		}
	};

	const validateForm = () => {
		return validateFormWithData(formData);
	};

	const validateFormWithData = (data) => {
		if (!data.name.trim()) {
			return { valid: false, error: 'Client name is required' };
		}
		if (data.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
			return { valid: false, error: 'Please enter a valid email address' };
		}
		return { valid: true };
	};

	const handleSubmit = async (e) => {
		if (e) e.preventDefault();

		const validation = validateForm();
		if (!validation.valid) {
			return;
		}

		const result = await createClient(formData);

		if (result.success) {
			// Refresh client list
			await fetchClients();

			// Close modal and reset form (only if not in onboarding mode)
			if (!isOnboarding) {
				setIsOpen(false);
				setFormData({
					name: '',
					email: '',
					phone: '',
					category: 'individual',
					priority: 'medium',
					notes: ''
				});
			}

			// Call success callback if provided
			if (onSuccess) {
				onSuccess(result.data);
			}
		}
	};

	const handleCancel = () => {
		setIsOpen(false);
		setFormData({
			name: '',
			email: '',
			phone: '',
			category: 'individual',
			priority: 'medium',
			notes: ''
		});
		clearError();
	};

	// If in onboarding mode, render the form content directly without modal wrapper
	if (isOnboarding) {
		return (
			<form onSubmit={handleSubmit}>
				<div className="space-y-6">
					{error && (
						<Alert variant="error">
							{error}
						</Alert>
					)}

					<FormField
						label="Client Name *"
						helpText="The name of the person or organization you'll be monitoring"
						error={error && !formData.name.trim() ? 'Name is required' : undefined}
					>
						<Input
							placeholder="e.g., John Smith or Acme Corp"
							value={formData.name}
							onChange={(e) => handleInputChange('name', e.target.value)}
							required
							className="text-base"
						/>
					</FormField>

					<div className="grid grid-cols-2 gap-6">
						<FormField
							label="Category"
							helpText="Helps us tailor monitoring settings for different client types"
						>
							<select
								className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-base"
								value={formData.category}
								onChange={(e) => handleInputChange('category', e.target.value)}
							>
								{categoryOptions.map(option => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</FormField>

						<FormField
							label="Priority"
							helpText="Determines how frequently we'll scan and alert you to issues"
						>
							<select
								className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent text-base"
								value={formData.priority}
								onChange={(e) => handleInputChange('priority', e.target.value)}
							>
								{priorityOptions.map(option => (
									<option key={option.value} value={option.value}>
										{option.label}
									</option>
								))}
							</select>
						</FormField>
					</div>

					{/* Add visual separator and helpful tip */}
					<div className="bg-brand-50 border border-brand-200 rounded-lg p-4 dark:bg-brand-500/10 dark:border-brand-500/30">
						<div className="flex items-start space-x-3">
							<div className="flex-shrink-0">
								<svg className="h-5 w-5 text-brand-400" fill="currentColor" viewBox="0 0 20 20">
									<path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
								</svg>
							</div>
							<div>
								<h4 className="text-sm font-medium text-brand-800">Pro Tip</h4>
								<p className="text-sm text-brand-700 mt-1">
									Don't worry - you can always change these settings later. We'll help you fine-tune monitoring based on your specific needs.
								</p>
							</div>
						</div>
					</div>
				</div>
			</form>
		);
	}

	// Regular modal mode for non-onboarding usage
	return (
		<Modal open={isOpen} onOpenChange={setIsOpen}>
			{trigger && (
				<ModalTrigger asChild>
					{trigger}
				</ModalTrigger>
			)}

			<ModalContent className="max-w-md">
				<ModalHeader>
					<ModalTitle>Add New Client</ModalTitle>
					<ModalDescription>
						Create a new client to start monitoring their social media presence.
					</ModalDescription>
				</ModalHeader>

				<form onSubmit={handleSubmit}>
					<CardContent className="space-y-4">
						{error && (
							<Alert variant="error">
								{error}
							</Alert>
						)}

						<FormField
							label="Client Name *"
							error={error && !formData.name.trim() ? 'Name is required' : undefined}
						>
							<Input
								placeholder="Enter client name"
								value={formData.name}
								onChange={(e) => handleInputChange('name', e.target.value)}
								required
							/>
						</FormField>

						<FormField
							label="Email"
							helpText="Optional - for contact information"
						>
							<Input
								type="email"
								placeholder="client@example.com"
								value={formData.email}
								onChange={(e) => handleInputChange('email', e.target.value)}
							/>
						</FormField>

						<FormField
							label="Phone"
							helpText="Optional - for contact information"
						>
							<Input
								type="tel"
								placeholder="+1 (555) 123-4567"
								value={formData.phone}
								onChange={(e) => handleInputChange('phone', e.target.value)}
							/>
						</FormField>

						<div className="grid grid-cols-2 gap-4">
							<FormField label="Category">
								<select
									className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
									value={formData.category}
									onChange={(e) => handleInputChange('category', e.target.value)}
								>
									{categoryOptions.map(option => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</FormField>

							<FormField label="Priority">
								<select
									className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
									value={formData.priority}
									onChange={(e) => handleInputChange('priority', e.target.value)}
								>
									{priorityOptions.map(option => (
										<option key={option.value} value={option.value}>
											{option.label}
										</option>
									))}
								</select>
							</FormField>
						</div>

						<FormField
							label="Notes"
							helpText="Optional - additional information about the client"
						>
							<textarea
								className="w-full px-3 py-2 border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent resize-none"
								rows={3}
								placeholder="Enter any additional notes..."
								value={formData.notes}
								onChange={(e) => handleInputChange('notes', e.target.value)}
							/>
						</FormField>
					</CardContent>

					<ModalFooter>
						<ModalClose asChild>
							<Button variant="secondary" onClick={handleCancel}>
								Cancel
							</Button>
						</ModalClose>
						<Button type="submit" loading={loading}>
							Create Client
						</Button>
					</ModalFooter>
				</form>
			</ModalContent>
		</Modal>
	);
}); 